import React from "react";
import { API_BASE } from "../../../../utils/api";

/**
 * CategoryEditModal Component
 * Modal for editing category details with multi-language support
 */
const CategoryEditModal = ({
  isOpen,
  onClose,
  category,
  onSubmit,
  editOtherLang,
  setEditOtherLang,
  languages,
  selectedLang,
  setSelectedLang,
  translations,
  setTranslations,
  newImage,
  setNewImage,
  printerCategories,
  enablePrintCat,
  printKey,
  setPrintKey,
  isVisible,
  setIsVisible,
}) => {
  if (!isOpen || !category) return null;

  const handleInputChange = (field, value) => {
    setTranslations((prev) => ({
      ...prev,
      [selectedLang]: {
        ...prev[selectedLang],
        [field]: value,
      },
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
    }
  };

  const handleLangSwitch = (langCode) => {
    setSelectedLang(langCode);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-semibold text-gray-800">Edit Category</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Multi-language Toggle */}
          {languages && languages.length > 0 && (
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={editOtherLang}
                onChange={(e) => setEditOtherLang(e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Edit another language
              </span>
            </label>
          )}

          {/* Language Selection */}
          {editOtherLang && (
            <div className="flex flex-wrap gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleLangSwitch(lang.code)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedLang === lang.code
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          )}

          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Name ({selectedLang.toUpperCase()})
            </label>
            <input
              type="text"
              value={translations[selectedLang]?.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter category name"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description ({selectedLang.toUpperCase()})
            </label>
            <textarea
              value={translations[selectedLang]?.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              placeholder="Enter description"
            />
          </div>

          {/* Printer Category */}
          {enablePrintCat && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Print Location (Optional)
              </label>
              <select
                value={printKey || "kitchen"}
                onChange={(e) => setPrintKey(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
              >
                {printerCategories.map((pc) => (
                  <option key={pc.key} value={pc.key}>
                    {pc.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Current Image */}
          {category.images && category.images.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Image
              </label>
              <img
                src={`${API_BASE}${category.images[0]}`}
                alt={category.name}
                className="w-40 h-40 object-cover rounded-lg border-2 border-gray-200"
              />
            </div>
          )}

          {/* New Image Preview */}
          {newImage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Image Preview
              </label>
              <img
                src={URL.createObjectURL(newImage)}
                alt="New"
                className="w-40 h-40 object-cover rounded-lg border-2 border-gray-200"
              />
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload New Image
            </label>
            <input
              type="file"
              accept="image/png, image/jpeg"
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {/* Is Available Checkbox */}
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Is Available
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryEditModal;
